/*
  Wrapped so nothing in here is declared globally. Older revisions of the curated kit page template
  inline their own copy of this code, and two classic scripts that both declare a top-level `let` of
  the same name make the second one fail to parse, which would silently disable the whole popup.
  Everything the inline HTML calls is published on `window` at the bottom instead.
*/
(() => {
// Modal state
  let curatedKitCurrentSlide = 0;
  let curatedKitTotalSlides = 0;
  const curatedKitSiblingListCache = new Map();
  const curatedKitProductCache = new Map();
  const CURATED_KIT_MODAL_IMAGE_WIDTH = 800;
  const CURATED_KIT_MODAL_ZOOM_WIDTH = 2200;
  const CURATED_KIT_MODAL_SWATCH_WIDTH = 120;

  function resizeCuratedKitImageUrl(url, width) {
    if (!url || typeof url !== 'string' || !width) return url;
    try {
      const wasProtocolRelative = url.startsWith('//');
      const absolute = wasProtocolRelative ? ('https:' + url) : url;
      const parsed = new URL(absolute, window.location.origin);
      parsed.searchParams.set('width', String(width));
      return wasProtocolRelative ? parsed.href.replace(/^https?:/, '') : parsed.href;
    } catch (e) {
      const separator = url.indexOf('?') === -1 ? '?' : '&';
      return url + separator + 'width=' + width;
    }
  }

  function getCuratedKitDisplayImageUrl(url, width) {
    if (!window.matchMedia('(max-width: 699px)').matches) return url;
    return resizeCuratedKitImageUrl(url, width);
  }

  const CURATED_KIT_SIBLING_GROUPS = [
    ['welt-trouser-black', 'welt-trouser-navy', 'welt-trouser-stone'],
    ['driggs-5-pocket-black', 'driggs-5-pocket-navy', 'driggs-5-pocket-stone'],
    ['english-standard-white', 'english-standard-light-blue', 'english-standard-blue-white-stripe'],
    ['austin-oxford-light-grey', 'austin-oxford-olive', 'austin-oxford-navy', 'austin-oxford-light-beige']
  ];

  const CURATED_KIT_FALLBACK_TITLES = {
    'welt-trouser-black': 'Welt Trouser - Black',
    'welt-trouser-navy': 'Welt Trouser - Navy',
    'welt-trouser-stone': 'Welt Trouser - Stone',
    'driggs-5-pocket-black': 'Driggs 5 Pocket - Black',
    'driggs-5-pocket-navy': 'Driggs 5 Pocket - Navy',
    'driggs-5-pocket-stone': 'Driggs 5 Pocket - Stone',
    'english-standard-white': 'English Standard - White',
    'english-standard-light-blue': 'English Standard - Light Blue',
    'english-standard-blue-white-stripe': 'English Standard - Blue/White Striped',
    'austin-oxford-light-grey': 'Austin Oxford - Light Grey',
    'austin-oxford-olive': 'Austin Oxford - Olive',
    'austin-oxford-navy': 'Austin Oxford - Navy',
    'austin-oxford-light-beige': 'Austin Oxford - Light Beige'
  };

  // Color swatch styles — by handle (reliable) and by color name (fallback).
  const curatedKitModalSwatchStylesByHandle = {
    'english-standard-white': 'background-color: #ffffff;',
    'english-standard-light-blue': 'background-color: #b9c8e0;',
    'english-standard-blue-white-stripe': 'background: linear-gradient(45deg, #ffffff 50%, #b9c8e0 50%);',
    'english-standard-blue-white-striped': 'background: linear-gradient(45deg, #ffffff 50%, #b9c8e0 50%);',
    'welt-trouser-black': 'background-color: #141414;',
    'welt-trouser-navy': 'background-color: #101f2b;',
    'welt-trouser-stone': 'background-color: #bbaa98;',
    'driggs-5-pocket-black': 'background-color: #141414;',
    'driggs-5-pocket-navy': 'background-color: #101f2b;',
    'driggs-5-pocket-stone': 'background-color: #bbaa98;',
    'austin-oxford-light-grey': 'background-color: #b6aca7;',
    'austin-oxford-olive': 'background-color: #4c5446;',
    'austin-oxford-navy': 'background-color: #101f2b;',
    'austin-oxford-light-beige': 'background-color: #b8a695;'
  };

  const curatedKitModalColorSwatchStyles = {
    black: 'background-color: #111111;',
    navy: 'background-color: #101f2b;',
    stone: 'background-color: #bbaa98;',
    white: 'background-color: #ffffff;',
    'light blue': 'background-color: #b9c8e0;',
    'blue/white striped': 'background: linear-gradient(45deg, #ffffff 50%, #b9c8e0 50%);',
    'blue white striped': 'background: linear-gradient(45deg, #ffffff 50%, #b9c8e0 50%);',
    'blue/white stripe': 'background: linear-gradient(45deg, #ffffff 50%, #b9c8e0 50%);',
    'light grey': 'background-color: #b6aca7;',
    olive: 'background-color: #4c5446;',
    'light beige': 'background-color: #b8a695;'
  };

  function findCuratedKitSiblingGroup(handle) {
    if (!handle || typeof handle !== 'string') return null;
    handle = resolveCuratedKitProductHandle(handle);
    return CURATED_KIT_SIBLING_GROUPS.find((group) => group.includes(handle)) || null;
  }

  async function fetchCuratedKitProductByHandle(handle) {
    if (curatedKitProductCache.has(handle)) {
      return curatedKitProductCache.get(handle);
    }
    try {
      const siblingResponse = await fetch(`/products/${handle}.json`);
      if (siblingResponse.ok) {
        const siblingData = await siblingResponse.json();
        curatedKitProductCache.set(handle, siblingData.product);
        return siblingData.product;
      }
    } catch (e) {
      console.warn(`Error fetching curated kit product "${handle}":`, e);
    }
    return { handle, title: CURATED_KIT_FALLBACK_TITLES[handle] || handle };
  }

  async function loadCuratedKitSiblings(product, productHandle) {
    const cacheKey = product.handle || productHandle;
    if (curatedKitSiblingListCache.has(cacheKey)) {
      const cachedSiblings = curatedKitSiblingListCache.get(cacheKey);
      if (Array.isArray(cachedSiblings)) {
        return cachedSiblings.filter(Boolean);
      }
      return cachedSiblings ? [cachedSiblings] : [product];
    }

    const resolvedHandle = resolveCuratedKitProductHandle(product.handle || productHandle);
    const matchedGroup = findCuratedKitSiblingGroup(resolvedHandle);
    let siblings = [];

    if (matchedGroup) {
      const fetchedSiblings = await Promise.all(
        matchedGroup.map((handle) => fetchCuratedKitProductByHandle(handle))
      );
      siblings = matchedGroup.map((handle, index) => {
        const sibling = fetchedSiblings[index];
        if (sibling && sibling.handle) return sibling;
        return { handle, title: CURATED_KIT_FALLBACK_TITLES[handle] || handle };
      });
    } else {
      siblings = [product];
    }

    curatedKitSiblingListCache.set(cacheKey, siblings);
    return siblings;
  }

  function formatCuratedKitModalPrice(product) {
    const handle = product?.handle || '';
    if (handle.indexOf('welt-trouser') !== -1 || handle.indexOf('driggs-5-pocket') !== -1) {
      return '110.00';
    }
    const variant = product?.variants?.[0];
    if (!variant || variant.price == null) return null;
    const dollars = parseFloat(String(variant.price).replace(/[^0-9.]/g, ''));
    return Number.isFinite(dollars) ? dollars.toFixed(2) : null;
  }

  function getCuratedKitModalSwatchStyle(colorName, productHandle) {
    if (productHandle && curatedKitModalSwatchStylesByHandle[productHandle]) {
      return curatedKitModalSwatchStylesByHandle[productHandle];
    }
    if (!colorName || typeof colorName !== 'string') {
      return null;
    }
    const key = colorName.trim().toLowerCase();
    return curatedKitModalColorSwatchStyles[key] || null;
  }

  function escapeCuratedKitModalHtml(str) {
    return String(str)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');
  }

  function curatedKitRichTextNodeToHtml(node) {
    if (!node) return '';

    if (node.type === 'text') {
      let text = escapeCuratedKitModalHtml(node.value || '');
      if (node.bold) text = `<strong>${text}</strong>`;
      if (node.italic) text = `<em>${text}</em>`;
      return text;
    }

    if (node.type === 'paragraph') {
      return `<p>${(node.children || []).map(curatedKitRichTextNodeToHtml).join('')}</p>`;
    }

    if (node.type === 'heading') {
      const level = node.level || 3;
      return `<h${level}>${(node.children || []).map(curatedKitRichTextNodeToHtml).join('')}</h${level}>`;
    }

    if (node.type === 'list') {
      const tag = node.listType === 'ordered' ? 'ol' : 'ul';
      return `<${tag}>${(node.children || []).map(curatedKitRichTextNodeToHtml).join('')}</${tag}>`;
    }

    if (node.type === 'list-item') {
      return `<li>${(node.children || []).map(curatedKitRichTextNodeToHtml).join('')}</li>`;
    }

    if (node.type === 'link') {
      const href = escapeCuratedKitModalHtml(node.url || '#');
      return `<a href="${href}">${(node.children || []).map(curatedKitRichTextNodeToHtml).join('')}</a>`;
    }

    if (Array.isArray(node.children)) {
      return node.children.map(curatedKitRichTextNodeToHtml).join('');
    }

    return '';
  }

  function formatCuratedKitMetafieldContent(value) {
    if (value == null || value === '') return '';
    if (typeof value === 'string') return value;
    if (typeof value === 'object') {
      if (value.type === 'root' && Array.isArray(value.children)) {
        return curatedKitRichTextNodeToHtml(value);
      }
      if (value.value != null) {
        return formatCuratedKitMetafieldContent(value.value);
      }
    }
    return '';
  }

  const CURATED_KIT_HANDLE_ALIASES = {
    'english-standard-blue-white-striped': 'english-standard-blue-white-stripe'
  };

  function resolveCuratedKitProductHandle(handle) {
    if (!handle || typeof handle !== 'string') return handle;
    return CURATED_KIT_HANDLE_ALIASES[handle] || handle;
  }

  function getCuratedKitFallbackHandle(handle) {
    if (!handle || typeof handle !== 'string') {
      return null;
    }

    handle = resolveCuratedKitProductHandle(handle);

    if (handle.indexOf('english-standard') !== -1) return 'english-standard-white';
    if (handle.indexOf('austin-oxford') !== -1) return 'austin-oxford-light-grey';
    if (handle.indexOf('welt-trouser') !== -1) return 'welt-trouser-black';
    if (handle.indexOf('driggs-5-pocket') !== -1) return 'driggs-5-pocket-black';

    return null;
  }
  
  /*
    Sibling assets are resolved from this file's own URL, so the popup can pull in its stylesheet
    on pages that never render the kit sections (the homepage, collections) without anything
    else having to know the theme's CDN path.
  */
  const CURATED_KIT_ASSET_BASE = (function () {
    const src = (document.currentScript && document.currentScript.src) || '';
    return src.replace(/curated-kit-product-modal\.js.*$/, '');
  })();

  function ensureCuratedKitAssets() {
    if (!CURATED_KIT_ASSET_BASE) return;

    if (!document.querySelector('link[href*="waldruh-kit-builder"], style[data-curated-kit-styles]')) {
      const link = document.createElement('link');
      link.rel = 'stylesheet';
      link.href = `${CURATED_KIT_ASSET_BASE}waldruh-kit-builder.css`;
      document.head.appendChild(link);
    }

    if (!document.querySelector('script[src*="kit-builder-image-zoom"]')) {
      const script = document.createElement('script');
      script.type = 'module';
      script.src = `${CURATED_KIT_ASSET_BASE}kit-builder-image-zoom.js`;
      document.head.appendChild(script);
    }
  }

  /*
    The popup builds its own container instead of relying on markup being rendered into the page.
    Quick buy runs on pages that render none of the kit sections, and the container has to live at
    body level to stack above the quick-buy sheet.
  */
  function ensureCuratedKitModal() {
    ensureCuratedKitAssets();
    if (document.getElementById('curated-kit-modal')) return;

    const overlay = document.createElement('div');
    overlay.className = 'kit-builder__modal-overlay';
    overlay.id = 'curated-kit-modal-overlay';
    overlay.style.zIndex = '10060';
    overlay.addEventListener('click', () => closeCuratedKitModal());

    const modal = document.createElement('div');
    modal.className = 'kit-builder__modal';
    modal.id = 'curated-kit-modal';
    modal.style.zIndex = '10061';
    modal.innerHTML = [
      '<div class="kit-builder__modal-header">',
      '<span class="kit-builder__modal-header-title">Product details</span>',
      '<button type="button" class="kit-builder__modal-close" aria-label="Close">',
      '<svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">',
      '<line x1="18" y1="6" x2="6" y2="18"></line><line x1="6" y1="6" x2="18" y2="18"></line>',
      '</svg></button></div>',
      '<div class="kit-builder__modal-body" id="curated-kit-modal-content"></div>'
    ].join('');
    modal.querySelector('.kit-builder__modal-close').addEventListener('click', () => closeCuratedKitModal());

    document.body.appendChild(overlay);
    document.body.appendChild(modal);

    // Gestures normally bind at page load, so a container created this late has to ask for them.
    if (typeof window.waldruhSetupKitModal === 'function') window.waldruhSetupKitModal('curated-kit-modal');
  }

  /*
    Fit/Fabric/Care come from metafields, which only Liquid can read. Any page that can open the
    popup embeds them as JSON, including the quick-buy payload, so collect whatever is present.
  */
  function collectCuratedKitMetafields() {
    window.curatedKitMetafields = window.curatedKitMetafields || {};

    document.querySelectorAll('script[data-curated-kit-metafields]').forEach((node) => {
      if (node.dataset.curatedKitMetafieldsRead === '1') return;
      node.dataset.curatedKitMetafieldsRead = '1';
      try {
        Object.assign(window.curatedKitMetafields, JSON.parse(node.textContent || '{}'));
      } catch (error) {
        /* a malformed block just leaves the accordions empty */
      }
    });
  }

  /*
    Prestige's quick buy traps focus inside <quick-buy-modal> and treats any click outside it as
    "dismiss me". This popup sits at body level, so without pausing that trap the first tap inside
    the popup would close the kit sheet underneath and lose the sizes already chosen. Pausing also
    stops the trap from pulling focus back out of the popup.
  */
  let curatedKitPausedHost = null;

  function pauseHostDialogFocusTrap() {
    const host = document.querySelector('quick-buy-modal[open]');
    if (!host) return;

    try {
      host.focusTrap?.pause?.();
      curatedKitPausedHost = host;
    } catch (error) {
      /* no trap to pause: the popup just behaves as it does on the kit page */
    }
  }

  function resumeHostDialogFocusTrap() {
    if (!curatedKitPausedHost) return;

    try {
      curatedKitPausedHost.focusTrap?.unpause?.();
    } catch (error) {
      /* the host may already have closed, which deactivates its trap anyway */
    }
    curatedKitPausedHost = null;
  }

  // Open product modal for curated kit items
  async function openCuratedKitModal(productHandle) {
    productHandle = resolveCuratedKitProductHandle(productHandle);

    ensureCuratedKitModal();
    collectCuratedKitMetafields();
    pauseHostDialogFocusTrap();

    const overlay = document.getElementById('curated-kit-modal-overlay');
    const modal = document.getElementById('curated-kit-modal');
    const contentDiv = document.getElementById('curated-kit-modal-content');
    if (!overlay || !modal || !contentDiv) return;
    
    // Show loading state
    contentDiv.innerHTML = '<div style="display: flex; justify-content: center; align-items: center; height: 400px;"><div class="kit-builder__add-cart-loading" style="display: flex; align-items: center;"><svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" style="animation: spin 1s linear infinite; margin-right: 0.5rem;"><path d="M21 12a9 9 0 11-6.219-8.56"/></svg> Loading...</div></div>';
    
    // Show the modal
    overlay.classList.add('active');
    modal.classList.add('active');
    document.body.style.overflow = 'hidden';
    
    try {
      // Fetch product data using Shopify's JSON API.
      // Some curated kits can contain an outdated or unavailable sibling handle,
      // so we retry with the base product's default handle when needed.
      let resolvedHandle = productHandle;
      let response = await fetch(`/products/${resolvedHandle}.json`);

      if (!response.ok) {
        const fallbackHandle = getCuratedKitFallbackHandle(productHandle);

        if (fallbackHandle && fallbackHandle !== productHandle) {
          resolvedHandle = fallbackHandle;
          response = await fetch(`/products/${resolvedHandle}.json`);
        }
      }

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      const product = data.product;
      
      // Extract images from the product data (up to 5)
      const validImages = [];
      if (product.images && product.images.length > 0) {
        product.images.slice(0, 5).forEach((img) => {
          validImages.push({
            src: getCuratedKitDisplayImageUrl(img.src, CURATED_KIT_MODAL_IMAGE_WIDTH),
            zoomSrc: resizeCuratedKitImageUrl(img.src, CURATED_KIT_MODAL_ZOOM_WIDTH),
            alt: img.alt || product.title
          });
        });
      }
      
      // Get description text (strip HTML tags, keep full text)
      let descriptionText = '';
      if (product.body_html) {
        const tempDiv = document.createElement('div');
        tempDiv.innerHTML = product.body_html;
        descriptionText = tempDiv.textContent.trim();
      }
      
      // Get product details for accordions from pre-loaded metafields
      let details = { fit: '', fabric: '', care: '' };
      const metafields = window.curatedKitMetafields || {};
      if (metafields[productHandle]) {
        details = { ...metafields[productHandle] };
      } else if (metafields[product.handle]) {
        details = { ...metafields[product.handle] };
      }

      const fitContent = formatCuratedKitMetafieldContent(details.fit);
      const fabricContent = formatCuratedKitMetafieldContent(details.fabric);
      const careContent = formatCuratedKitMetafieldContent(details.care);
      
      const siblings = await loadCuratedKitSiblings(product, productHandle);
      
      const productTitle = product.title.split(' - ')[0];
      curatedKitTotalSlides = validImages.length;
      curatedKitCurrentSlide = 0;
      
      // Build two-column layout (matching kit-customization modal exactly)
      let htmlContent = '<div class="kit-builder__modal-split">';
      
      // LEFT SIDE - Image Slider
      htmlContent += '<div class="kit-builder__modal-left">';
      if (validImages.length > 0) {
        htmlContent += '<div class="kit-builder__modal-slider">';
        
        // Slider navigation arrows
        if (curatedKitTotalSlides > 1) {
          htmlContent += `
            <button class="kit-builder__modal-slider-nav kit-builder__modal-slider-nav--prev" onclick="curatedKitSlideModal(-1)">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><polyline points="15,18 9,12 15,6"></polyline></svg>
            </button>
            <button class="kit-builder__modal-slider-nav kit-builder__modal-slider-nav--next" onclick="curatedKitSlideModal(1)">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><polyline points="9,18 15,12 9,6"></polyline></svg>
            </button>
          `;
        }
        
        // Slides container
        htmlContent += '<div class="kit-builder__modal-slides" id="curated-kit-slides">';
        validImages.forEach((img, index) => {
          const loading = index === 0 ? 'eager' : 'lazy';
          htmlContent += `<div class="kit-builder__modal-slide"><img src="${escapeCuratedKitModalHtml(img.src)}" data-zoom-src="${escapeCuratedKitModalHtml(img.zoomSrc)}" alt="${escapeCuratedKitModalHtml(img.alt)}" loading="${loading}" decoding="async" width="${CURATED_KIT_MODAL_IMAGE_WIDTH}" role="button" tabindex="0" /></div>`;
        });
        htmlContent += '</div></div>';
        
        // Dots navigation
        if (curatedKitTotalSlides > 1) {
          htmlContent += '<div class="kit-builder__modal-dots" id="curated-kit-dots">';
          for (let i = 0; i < curatedKitTotalSlides; i++) {
            htmlContent += `<button class="kit-builder__modal-dot ${i === 0 ? 'active' : ''}" onclick="curatedKitGoToSlide(${i})"></button>`;
          }
          htmlContent += '</div>';
        }
      } else {
        htmlContent += '<div style="display: flex; align-items: center; justify-content: center; height: 100%; color: #999;">No images available</div>';
      }
      htmlContent += '</div>';
      
      // RIGHT SIDE - Details
      htmlContent += '<div class="kit-builder__modal-right">';
      htmlContent += `<h2 class="kit-builder__modal-title">${productTitle}</h2>`;
      
      // Price display
      const isPants = product.handle && (product.handle.indexOf('welt-trouser') !== -1 || product.handle.indexOf('driggs-5-pocket') !== -1);
      const isShirt = product.handle && (product.handle.indexOf('english-standard') !== -1 || product.handle.indexOf('austin-oxford') !== -1);
      const useColorSwatches = isPants || isShirt;
      const modalPrice = formatCuratedKitModalPrice(product);
      if (modalPrice) {
        htmlContent += `<p class="kit-builder__modal-price">$${modalPrice}</p>`;
      }
      
      // Colorway swatches (if siblings exist)
      if (siblings.length > 1) {
        const validSiblings = siblings.filter(s => s && s.handle && typeof s.handle === 'string' && s.handle.trim() !== '');
        
        if (validSiblings.length > 1) {
          htmlContent += '<div class="kit-builder__modal-colorways" style="margin: 1rem 0 1.5rem 0;">';
          htmlContent += '<div style="font-size: 0.75rem; text-transform: uppercase; letter-spacing: 0.05em; margin-bottom: 0.75rem; color: #666; font-weight: 500;">Colorways</div>';
          htmlContent += '<div class="kit-builder__modal-swatches" style="display: flex; gap: 0.5rem; flex-wrap: wrap;">';
          
          validSiblings.forEach((sibling) => {
            const siblingHandle = sibling.handle.trim();
            const siblingTitle = sibling.title || '';
            const colorName = siblingTitle.split(' - ').pop() || siblingTitle;
            const isCurrent = sibling.id === product.id || siblingHandle === product.handle;
            const siblingUrl = `/products/${siblingHandle}`;
            const swatchClass = isCurrent ? 'kit-builder__modal-swatch kit-builder__modal-swatch--active' : 'kit-builder__modal-swatch';
            const borderColor = isCurrent ? '#000' : '#ddd';
            const borderWidth = isCurrent ? '2px' : '1px';
            
            // Get image from sibling product
            let imgSrc = null;
            if (sibling.images && sibling.images.length > 0) {
              imgSrc = getCuratedKitDisplayImageUrl(sibling.images[0].src, CURATED_KIT_MODAL_SWATCH_WIDTH);
            } else if (sibling.featured_image) {
              const rawImageSrc = typeof sibling.featured_image === 'string' ? sibling.featured_image : sibling.featured_image.src;
              imgSrc = getCuratedKitDisplayImageUrl(rawImageSrc, CURATED_KIT_MODAL_SWATCH_WIDTH);
            }
            
            const escapedHandle = siblingHandle.replace(/'/g, "\\'");
            const swatchFillStyle = getCuratedKitModalSwatchStyle(colorName, siblingHandle);
            
            htmlContent += `<a href="${siblingUrl}" class="${swatchClass}" data-handle="${siblingHandle}" onclick="event.preventDefault(); updateCuratedKitModalImages('${escapedHandle}'); return false;" style="display: inline-block; width: 36px; height: 36px; border-radius: 50%; border: ${borderWidth} solid ${borderColor}; overflow: hidden; text-decoration: none; cursor: pointer; transition: border-color 0.2s ease;" title="${colorName}">`;
            if (useColorSwatches) {
              if (swatchFillStyle) {
                htmlContent += `<span aria-hidden="true" style="display: block; width: 100%; height: 100%; ${swatchFillStyle}"></span>`;
              } else {
                htmlContent += `<span aria-hidden="true" style="display: block; width: 100%; height: 100%; background: #f0f0f0;"></span>`;
              }
            } else if (imgSrc) {
              htmlContent += `<img src="${imgSrc}" alt="${colorName}" style="width: 100%; height: 100%; object-fit: cover; display: block;" />`;
            } else {
              htmlContent += `<div style="width: 100%; height: 100%; background: #f0f0f0; display: block;"></div>`;
            }
            htmlContent += `</a>`;
          });
          htmlContent += '</div></div>';
        }
      }
      
      // Accordions (matching kit-customization exactly)
      htmlContent += '<div class="kit-builder__modal-accordions">';
      
      // Description Accordion - OPEN BY DEFAULT
      htmlContent += `
        <div class="kit-builder__modal-accordion open" data-accordion>
          <button type="button" class="kit-builder__modal-accordion-header">
            <span>Description</span>
            <svg class="kit-builder__modal-accordion-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><polyline points="6,9 12,15 18,9"></polyline></svg>
          </button>
          <div class="kit-builder__modal-accordion-content">
            <div class="kit-builder__modal-accordion-body">${descriptionText || ''}</div>
          </div>
        </div>
      `;
      
      // Fit Accordion
      htmlContent += `
        <div class="kit-builder__modal-accordion" data-accordion>
          <button type="button" class="kit-builder__modal-accordion-header">
            <span>Fit</span>
            <svg class="kit-builder__modal-accordion-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><polyline points="6,9 12,15 18,9"></polyline></svg>
          </button>
          <div class="kit-builder__modal-accordion-content">
            <div class="kit-builder__modal-accordion-body">${fitContent}</div>
          </div>
        </div>
      `;
      
      // Fabric Accordion
      htmlContent += `
        <div class="kit-builder__modal-accordion" data-accordion>
          <button type="button" class="kit-builder__modal-accordion-header">
            <span>Fabric</span>
            <svg class="kit-builder__modal-accordion-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><polyline points="6,9 12,15 18,9"></polyline></svg>
          </button>
          <div class="kit-builder__modal-accordion-content">
            <div class="kit-builder__modal-accordion-body">${fabricContent}</div>
          </div>
        </div>
      `;
      
      
      htmlContent += '</div>'; // End accordions
      
      // View full page link
      htmlContent += `<a href="/products/${product.handle}" target="_blank" class="kit-builder__modal-link">View Full Product Page</a>`;
      
      htmlContent += '</div>'; // End right side
      htmlContent += '</div>'; // End split
      
      contentDiv.innerHTML = htmlContent;
      
    } catch (error) {
      console.error('Error loading product details:', error);
      contentDiv.innerHTML = `
        <div style="display: flex; align-items: center; justify-content: center; height: 400px; flex-direction: column; text-align: center; padding: 2rem;">
          <p style="margin-bottom: 0.5rem; color: #d32f2f; font-weight: 500;">Unable to load product details</p>
          <p style="margin-bottom: 1rem; color: #666; font-size: 0.85rem;">Please try again.</p>
          <a href="/products/${productHandle}" target="_blank" class="kit-builder__modal-link">Try Viewing Product Page →</a>
        </div>`;
    }
  }
  
  // Update modal images when clicking colorway swatches
  async function updateCuratedKitModalImages(productHandle) {
    productHandle = resolveCuratedKitProductHandle(productHandle);

    const modal = document.getElementById('curated-kit-modal');
    if (!modal || !modal.classList.contains('active')) {
      return openCuratedKitModal(productHandle);
    }
    
    try {
      // Fetch new product data
      const response = await fetch(`/products/${productHandle}.json`);
      if (!response.ok) throw new Error('Failed to fetch');
      
      const data = await response.json();
      const product = data.product;
      
      // Update images
      const slidesContainer = document.getElementById('curated-kit-slides');
      const dotsContainer = document.getElementById('curated-kit-dots');
      
      if (slidesContainer && product.images && product.images.length > 0) {
        let slidesHtml = '';
        product.images.slice(0, 5).forEach((img) => {
          const displaySrc = getCuratedKitDisplayImageUrl(img.src, CURATED_KIT_MODAL_IMAGE_WIDTH);
          const zoomSrc = resizeCuratedKitImageUrl(img.src, CURATED_KIT_MODAL_ZOOM_WIDTH);
          slidesHtml += `<div class="kit-builder__modal-slide"><img src="${escapeCuratedKitModalHtml(displaySrc)}" data-zoom-src="${escapeCuratedKitModalHtml(zoomSrc)}" alt="${escapeCuratedKitModalHtml(img.alt || product.title)}" loading="lazy" decoding="async" width="${CURATED_KIT_MODAL_IMAGE_WIDTH}" role="button" tabindex="0" /></div>`;
        });
        slidesContainer.innerHTML = slidesHtml;
        slidesContainer.style.transform = 'translateX(0)';
        curatedKitCurrentSlide = 0;
        curatedKitTotalSlides = Math.min(product.images.length, 5);
        
        // Update dots
        if (dotsContainer) {
          let dotsHtml = '';
          for (let i = 0; i < curatedKitTotalSlides; i++) {
            dotsHtml += `<button class="kit-builder__modal-dot ${i === 0 ? 'active' : ''}" onclick="curatedKitGoToSlide(${i})"></button>`;
          }
          dotsContainer.innerHTML = dotsHtml;
        }
      }
      
      // Update active swatch
      const swatches = modal.querySelectorAll('.kit-builder__modal-swatch');
      swatches.forEach(swatch => {
        const isActive = swatch.dataset.handle === productHandle;
        swatch.style.borderColor = isActive ? '#000' : '#ddd';
        swatch.style.borderWidth = isActive ? '2px' : '1px';
        swatch.classList.toggle('kit-builder__modal-swatch--active', isActive);
      });
      
      // Update title
      const titleEl = modal.querySelector('.kit-builder__modal-title');
      if (titleEl) {
        titleEl.textContent = product.title.split(' - ')[0];
      }
      
      // Update price
      const priceEl = modal.querySelector('.kit-builder__modal-price');
      const modalPrice = formatCuratedKitModalPrice(product);
      if (priceEl && modalPrice) {
        priceEl.textContent = `$${modalPrice}`;
      }
      
      // Update description
      const descEl = modal.querySelector('.kit-builder__modal-accordion.open .kit-builder__modal-accordion-body');
      if (descEl && product.body_html) {
        const tempDiv = document.createElement('div');
        tempDiv.innerHTML = product.body_html;
        descEl.textContent = tempDiv.textContent.trim();
      }
      
      // Update view link
      const linkEl = modal.querySelector('.kit-builder__modal-link');
      if (linkEl) {
        linkEl.href = `/products/${product.handle}`;
      }
      
    } catch (error) {
      console.error('Error updating modal:', error);
      openCuratedKitModal(productHandle);
    }
  }
  
  function closeCuratedKitModal() {
    const overlay = document.getElementById('curated-kit-modal-overlay');
    const modal = document.getElementById('curated-kit-modal');
    if (!overlay || !modal) return;

    overlay.classList.remove('active');
    modal.classList.remove('active');
    resumeHostDialogFocusTrap();
    // If this was opened from a kit quick buy, leave scroll locked for the sheet underneath.
    if (!document.querySelector('quick-buy-modal[open]')) {
      document.body.style.overflow = '';
    }
    curatedKitCurrentSlide = 0;
  }
  window.closeCuratedKitModal = closeCuratedKitModal;

  const curatedKitModalElement = document.getElementById('curated-kit-modal');
  if (curatedKitModalElement) {
    let curatedKitTouchStartX = 0;
    let curatedKitTouchStartY = 0;
    let curatedKitAxis = null;

    curatedKitModalElement.addEventListener('touchstart', function(event) {
      if (!event.target.closest('.kit-builder__modal-slider')) return;
      curatedKitTouchStartX = event.changedTouches[0].screenX;
      curatedKitTouchStartY = event.changedTouches[0].screenY;
      curatedKitAxis = null;
    }, { passive: true });

    curatedKitModalElement.addEventListener('touchmove', function(event) {
      if (!event.target.closest('.kit-builder__modal-slider')) return;
      const deltaX = event.changedTouches[0].screenX - curatedKitTouchStartX;
      const deltaY = event.changedTouches[0].screenY - curatedKitTouchStartY;
      if (!curatedKitAxis) {
        if (Math.abs(deltaX) < 8 && Math.abs(deltaY) < 8) return;
        curatedKitAxis = Math.abs(deltaX) > Math.abs(deltaY) ? 'x' : 'y';
      }
      if (curatedKitAxis === 'x') event.preventDefault();
    }, { passive: false });

    curatedKitModalElement.addEventListener('touchend', function(event) {
      if (!event.target.closest('.kit-builder__modal-slider') || curatedKitAxis !== 'x') return;
      const deltaX = event.changedTouches[0].screenX - curatedKitTouchStartX;
      if (Math.abs(deltaX) < 40) return;

      window.kitProductModalDidSwipeUntil = Date.now() + 500;
      curatedKitSlideModal(deltaX > 0 ? -1 : 1);
    }, { passive: true });
  }
  
  function curatedKitSlideModal(direction) {
    curatedKitCurrentSlide += direction;
    if (curatedKitCurrentSlide < 0) curatedKitCurrentSlide = curatedKitTotalSlides - 1;
    if (curatedKitCurrentSlide >= curatedKitTotalSlides) curatedKitCurrentSlide = 0;
    curatedKitGoToSlide(curatedKitCurrentSlide);
  }
  
  function curatedKitGoToSlide(index) {
    const slides = document.getElementById('curated-kit-slides');
    const dots = document.querySelectorAll('#curated-kit-dots .kit-builder__modal-dot');
    
    if (slides) {
      curatedKitCurrentSlide = index;
      slides.style.transform = `translateX(-${index * 100}%)`;
      
      dots.forEach((dot, i) => {
        dot.classList.toggle('active', i === index);
      });
    }
  }
  
  function animateCuratedKitAccordionSwitch(container, targetAccordion) {
    const currentlyOpen = container.querySelector('.kit-builder__modal-accordion.open');
    if (!currentlyOpen) {
      targetAccordion.classList.add('open');
      return;
    }

    if (container.dataset.accordionAnimating === 'true') return;
    container.dataset.accordionAnimating = 'true';

    const reducedMotion = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const closingContent = currentlyOpen.querySelector('.kit-builder__modal-accordion-content');
    currentlyOpen.classList.remove('open');

    const openTarget = () => {
      targetAccordion.classList.add('open');
      container.dataset.accordionAnimating = 'false';
    };

    if (reducedMotion || !closingContent) {
      openTarget();
      return;
    }

    let finished = false;
    const finishClose = () => {
      if (finished) return;
      finished = true;
      closingContent.removeEventListener('transitionend', onCloseEnd);
      requestAnimationFrame(openTarget);
    };

    const onCloseEnd = (event) => {
      if (event.target !== closingContent || event.propertyName !== 'grid-template-rows') return;
      finishClose();
    };

    closingContent.addEventListener('transitionend', onCloseEnd);

    const closeMs = parseFloat(getComputedStyle(container).getPropertyValue('--modal-accordion-close')) || 420;
    window.setTimeout(finishClose, closeMs + 40);
  }

  function toggleCuratedKitAccordion(button) {
    const accordion = button.closest('.kit-builder__modal-accordion');
    if (!accordion || accordion.classList.contains('open')) return;

    const container = accordion.closest('.kit-builder__modal-accordions');
    if (!container) return;

    animateCuratedKitAccordionSwitch(container, accordion);
  }

  window.toggleCuratedKitAccordion = toggleCuratedKitAccordion;

  document.addEventListener('click', function(e) {
    const header = e.target.closest('#curated-kit-modal .kit-builder__modal-accordion-header');
    if (!header) return;
    const modal = document.getElementById('curated-kit-modal');
    if (!modal || !modal.classList.contains('active')) return;
    e.preventDefault();
    toggleCuratedKitAccordion(header);
  });
  
  // Prevent swatch clicks from navigating to the PDP.
  // The swatches render as <a href="/products/..."> for styling/SEO, but we want to swap
  // the modal content in-place when they're clicked.
  document.addEventListener('click', function(e) {
    const swatch = e.target && e.target.closest ? e.target.closest('#curated-kit-modal .kit-builder__modal-swatch') : null;
    if (!swatch) return;

    // If the inline onclick already ran and prevented navigation, don't double-handle.
    if (e.defaultPrevented) return;

    const modal = document.getElementById('curated-kit-modal');
    if (!modal || !modal.classList.contains('active')) return;

    const handle = swatch.getAttribute('data-handle');
    if (!handle) return;

    e.preventDefault();
    e.stopPropagation();
    updateCuratedKitModalImages(handle);
  });

  // Close modal with Escape key
  document.addEventListener('keydown', function(e) {
    if (e.key !== 'Escape') return;
    const modal = document.getElementById('curated-kit-modal');
    if (modal && modal.classList.contains('active')) closeCuratedKitModal();
  });

/*
  Kit piece names open this popup instead of navigating to the piece's page. Delegated from the
  document because quick buy builds its content in a DOMParser document and adopts the nodes in,
  and an inline onclick on such a node is never compiled into a handler, so it silently does
  nothing. If the popup markup is missing the click is left alone and the link navigates.
*/
document.addEventListener('click', (event) => {
  const path = typeof event.composedPath === 'function' ? event.composedPath() : [];
  const origin = path[0] && path[0].closest ? path[0] : event.target;
  const link = origin && origin.closest ? origin.closest('[data-kit-piece-handle]') : null;
  if (!link) return;

  event.preventDefault();
  openCuratedKitModal(link.getAttribute('data-kit-piece-handle'));
});

window.openCuratedKitModal = openCuratedKitModal;
window.updateCuratedKitModalImages = updateCuratedKitModalImages;
window.curatedKitSlideModal = curatedKitSlideModal;
window.curatedKitGoToSlide = curatedKitGoToSlide;
window.closeCuratedKitModal = closeCuratedKitModal;
window.toggleCuratedKitAccordion = toggleCuratedKitAccordion;
})();

